import authService from '@/repository/services/v1/auth'
import type { Auth } from '@@/types/types'

export default defineNuxtRouteMiddleware(async () => {
  const { $apiClient, $cookieStore } = useNuxtApp()
  const { authenticated, login, logout } = useAuth()
  const isoFetch = useRequestFetch()

  const authCookies = $cookieStore.getAuthCookies('kebab')

  try {
    if (!authCookies) throw new AuthCookieError()

    $apiClient.setHeaders(authCookies)
    const user = await authService($apiClient)
      .validateToken()
      .then<Auth['user']>((res) => res.data)

    // Ensure an incomplete user cannot make authenticated requests
    if (user?.incompleteSignup === true) {
      throw new Error('incomplete signup')
    }

    if (user?.incompleteSignup === false) {
      // only login if user was unauthenticated to avoid user-info refetch
      if (!authenticated.value) {
        await login(user)
      }
      // algolia tokens
      if (
        !$cookieStore.getCookie('search_admin_token') &&
        user.algoliaAdminSearchKey
      ) {
        $cookieStore.setCookie('search_admin_token', user.algoliaAdminSearchKey)
      }
      if (
        !$cookieStore.getCookie('search_b2b2c_token') &&
        user.algoliaB2b2cSearchKey
      ) {
        $cookieStore.setCookie('search_b2b2c_token', user.algoliaB2b2cSearchKey)
      }
    }
  } catch (err) {
    // Attempt to refresh the credentials
    if (
      err instanceof AuthCookieError &&
      (import.meta.server || authenticated.value)
    ) {
      // 'http-only' cookies are sent by $fetch ONLY client-side
      const res = await isoFetch('/api/auth/refresh').catch(() => {})
      // stay logged-in if refresh went smoothly
      if (res?.ok) {
        const { 'access-token': accessToken, client, uid } = res.credentials
        $cookieStore.setAuthCookies(accessToken, client, uid)
        return login(res.data)
      }
    }
    await logout()
  }
})

class AuthCookieError extends Error {
  constructor() {
    super('no auth cookie')
  }
}
